// Shared number and date formats (MP-OS-163, MP-OS-191; the written standard is
// docs/ui-format-standard.md). English chrome: dot decimal and a NARROW NO-BREAK
// SPACE (U+202F) as thousands separator, so grounding checks parse every figure alike.
//
// - Compact (axes, tooltips): "60 M", "1.25 M", "512.3 k", "769.71". Never "60 000 k".
// - Full (tables, cards): every digit kept, "40 452 315.21".

import { displayLabel } from './displayLabels'

const THIN = '\u202f'
// Between a figure and its unit ("15.4 %", "12 345 CU", "60 M"): a NO-BREAK SPACE (U+00A0), so
// a narrow cell never wraps the unit onto its own line. Kept distinct from the U+202F digit
// grouper so a parser never reads the unit gap as part of the number.
export const UNIT_SPACE = '\u00a0'
const degroup = (s: string): string => s.replace(/,/g, THIN)
// Cached per (digits, minimum): building an Intl.NumberFormat is the expensive part of a
// format call, and axis ticks format the same few precisions over and over (MPD-46 #4).
const numberFormats = new Map<string, Intl.NumberFormat>()
const numberFormat = (digits: number, minimum = 0): Intl.NumberFormat => {
  const key = `${digits}/${minimum}`
  let nf = numberFormats.get(key)
  if (!nf) numberFormats.set(key, nf = new Intl.NumberFormat('en-GB', { minimumFractionDigits: minimum, maximumFractionDigits: digits }))
  return nf
}

// Round as Intl displays it (half-expand on the shortest decimal), not with toFixed, which
// rounds 1.65 down to 1.6 because of binary floating point.
const rounders = new Map<number, Intl.NumberFormat>()
const round = (value: number, digits: number): number => {
  if (!rounders.has(digits)) rounders.set(digits, new Intl.NumberFormat('en-US', { useGrouping: false, maximumFractionDigits: digits }))
  return Number(rounders.get(digits)!.format(value)) + 0
}

// Largest scale first. A value rounded up to the next scale's threshold ("1 000 k") moves up.
const SCALES = [[1e9, 'bn'], [1e6, 'M'], [1e3, 'k']] as const

export type CompactDigits = { k?: number; m?: number }

// One decimal in the k-range keeps adjacent axis ticks distinct ("1.7 k", "2.2 k"); two in
// the M/bn range keep nice ticks such as 1.25 M and 3.75 M from rounding into each other.
export function formatCompact(value: number, { k = 1, m = 2 }: CompactDigits = {}): string {
  if (!Number.isFinite(value)) return String(value)
  const v = value + 0 // -0 → 0
  const abs = Math.abs(v)
  for (let i = 0; i < SCALES.length; i++) {
    const [size, suffix] = SCALES[i]
    if (abs < size) continue
    const digits = suffix === 'k' ? k : m
    const scaled = round(v / size, digits)
    if (Math.abs(scaled) >= 1000 && i > 0) {
      const [up, upSuffix] = SCALES[i - 1]
      return `${degroup(numberFormat(m).format(v / up))}${UNIT_SPACE}${upSuffix}`
    }
    return `${degroup(numberFormat(digits).format(scaled))}${UNIT_SPACE}${suffix}`
  }
  const small = round(v, m)
  // 999.999 rounds to 1 000: show it in the k scale like every other thousand.
  if (Math.abs(small) >= 1000) return `${degroup(numberFormat(k).format(small / 1000))}${UNIT_SPACE}k`
  return degroup(numberFormat(m).format(small))
}

// Decimals needed so distinct ticks never collapse onto the same label. The removed k-scale
// code always kept one decimal for exactly this reason ("1.7 k" vs "2.2 k"); this picks however
// many each scale actually needs for the ticks in play (e.g. a narrow, non-zero-based M-scale
// span where two decimals round "1 000 000" and "1 002 500" to the same "1 M").
function axisDigits(ticks: readonly number[]): CompactDigits {
  const values = [...new Set(ticks.filter((t) => Number.isFinite(t)))]
  for (let d = 1; d <= 4; d++) {
    if (new Set(values.map((t) => formatCompact(t, { k: d, m: d }))).size >= values.length) return { k: d, m: d }
  }
  return { k: 4, m: 4 }
}

// Axis tick for any numeric chart value; categories pass through. `ticks`, when given, are
// every value sharing this axis — decimals are then chosen so no two distinct ticks render the
// same label. Without them (the common single-value call), behaviour is unchanged.
// Never hand this to Recharts as `tickFormatter` directly: Recharts calls tickFormatter(value,
// index), and the index would arrive as `ticks` (MPD-46 #1). Use axisFormatterFor instead; the
// Array.isArray guard keeps a stray index from throwing.
export function formatAxisCompact(value: number | string, ticks?: readonly number[]): string {
  if (typeof value !== 'number') return String(value)
  if (!Array.isArray(ticks) || ticks.length < 2) return formatCompact(value)
  return formatCompact(value, axisDigits(ticks))
}

// A one-argument axis formatter with the axis values already bound, for Recharts'
// tickFormatter. The decimals are computed once here, not once per tick (MPD-46 #4).
export function axisFormatterFor(values: readonly number[]): (value: number | string) => string {
  const digits = values.length < 2 ? undefined : axisDigits(values)
  return (value) => (typeof value === 'number' ? formatCompact(value, digits) : String(value))
}

// MPD-132 — the grid every Recharts nice tick lies on. Recharts (recharts-scale getNiceTickValues,
// tickCount 5, domain [0, auto]) steps by a multiple of 0.05 × 10^d (0.1 × 10^d when d is 1), d
// being the digit count of (max − min) / 4, and every tick is a multiple of that step. Its ticks
// are not the data values: a share curve of 0 and 0.0125 % gets ticks 0.0025, 0.005, 0.0075, which
// formatted with the data's two decimals read "0.01, 0.01, 0.01, 0, 0". Choosing the decimals for
// this grid keeps every label Recharts can draw distinct. Empty when the values span nothing.
export function niceTickGrid(values: readonly number[], tickCount = 5): number[] {
  const finite = values.filter((v) => Number.isFinite(v))
  if (!finite.length) return []
  const lo = Math.min(0, ...finite)
  const hi = Math.max(0, ...finite)
  const span = hi - lo
  if (!(span > 0)) return []
  const digitCount = Math.floor(Math.log10(span / (tickCount - 1))) + 1
  const unit = (digitCount === 1 ? 0.1 : 0.05) * 10 ** digitCount
  const grid: number[] = []
  // Up to one span past the data: Recharts may round the top tick up by a step.
  for (let k = Math.floor(lo / unit); k <= Math.ceil((hi + span) / unit); k++) grid.push(Number((k * unit).toPrecision(12)))
  return grid
}

// The default axis formatter for a chart's data: decimals chosen for the ticks Recharts will
// actually draw (niceTickGrid), so no two tick labels repeat on any chart, percent or volume.
export function chartAxisFormatter(values: readonly number[]): (value: number | string) => string {
  const grid = niceTickGrid(values)
  return axisFormatterFor(grid.length ? grid : values)
}

// Readable money for tooltips: "1.2 M SEK", "769.71 SEK".
export const formatSekCompact = (value: number): string => `${formatCompact(value, { k: 2, m: 2 })}${UNIT_SPACE}SEK`

// Full-precision money for tables and cards: always two decimals, grouped thousands. The
// caller adds the unit (a table column or card label already names SEK).
const FULL_MONEY = numberFormat(2, 2)
export const formatSek = (value: number): string => degroup(FULL_MONEY.format(value + 0))

// Whole counts (orders): grouped thousands, no decimals.
const COUNT = numberFormat(0)
export const formatCount = (value: number): string => degroup(COUNT.format(value + 0))

// ---------------------------------------------------------------------------------------------
// MP-OS-191 — the rest of the written standard (docs/ui-format-standard.md). One helper per
// kind of figure so every view says the same thing the same way.

// A value the source did not report. Never 0: zero is a real observation and prints as "0".
export const MISSING = '—'
export const orMissing = (value: number | null | undefined, format: (value: number) => string): string =>
  value == null || !Number.isFinite(value) ? MISSING : format(value)

// Volumes (CU, packages, doses): grouped thousands, decimals only where the source has them
// (a recount to standard CU can be fractional), at most three. theme.formatNumber is this.
const QUANTITY = numberFormat(3)
export const formatQuantity = (value: number): string => degroup(QUANTITY.format(value + 0))

// Customer units, the one volume unit across the dashboard: "12 345 CU".
export const formatCU = (value: number): string => `${formatQuantity(value)}${UNIT_SPACE}CU`

// Shares and changes: one decimal and a space before the sign, "15.4 %".
const PERCENT = numberFormat(1, 1)
export const formatPercent = (value: number, digits = 1): string =>
  `${degroup((digits === 1 ? PERCENT : numberFormat(digits, digits)).format(value + 0))}${UNIT_SPACE}%`

// MPD-132 — a share ("15.4 %"). One that rounds to 0 at the shown precision but has sales
// prints "<0.1 %" ("<0.01 %" at two decimals, "<1 %" at none), never "0 %": 3 CU of a large
// market is not nothing. A true 0 (no sales) stays "0.0 %"; a missing share is "—".
// `hasSales` defaults to value > 0; pass it where the source already rounded the share (a table
// row whose 1-decimal share_pct is 0 while its CU is not). `unit: false` drops the "%" for
// places whose label already names it (a chart tooltip "%: <0.01").
export type ShareFormat = { digits?: number; hasSales?: boolean; unit?: boolean }
export function formatShare(value: number | null | undefined, { digits = 1, hasSales, unit = true }: ShareFormat = {}): string {
  if (value == null || !Number.isFinite(value)) return MISSING
  const suffix = unit ? `${UNIT_SPACE}%` : ''
  if (round(value, digits) === 0) {
    if ((hasSales ?? value > 0) && value >= 0) return `<${numberFormat(digits).format(10 ** -digits)}${suffix}`
    return unit ? formatPercent(0, digits) : '0'
  }
  return unit ? formatPercent(value, digits) : degroup(numberFormat(digits).format(value + 0))
}

// A chart value on a percent axis (unit "%", "% of All White"): two decimals like the share
// series the API sends, "0.21 %", and "<0.01 %" for a tiny share (chart tooltips, callouts).
export const isPercentUnit = (unit: string | null | undefined): boolean => !!unit && unit.trim().startsWith('%')
export const formatShareValue = (value: number): string => formatShare(value, { digits: 2 })

// A signed change in text form uses a true minus sign (U+2212), never a hyphen: "+3.2 %", "−3.2 %".
export const MINUS = '−'
export const formatSignedPercent = (value: number, digits = 1): string =>
  `${value < 0 ? MINUS : '+'}${formatPercent(Math.abs(value), digits)}`

// MP-OS-206 — the direction of a percent change, decided on the DISPLAYED value (rounded to
// one decimal like formatPercent), so a change that prints as "0.0 %" is 'flat': grey, no
// arrow, no sign, and never "−0.0 %". 'none' means there is no comparison at all; each view
// keeps its own text for that ("—", "new", "No comparable period available.").
export type ChangeDirection = 'up' | 'down' | 'flat' | 'none'
export function changeDirection(pct: number | null | undefined): ChangeDirection {
  if (pct == null || !Number.isFinite(pct)) return 'none'
  const shown = round(pct, 1)
  return shown > 0 ? 'up' : shown < 0 ? 'down' : 'flat'
}

// MPD-132 — a top-list row's change against the previous period, one rule for every list and
// popup (the change column and the rank's parenthesis):
// - 'none'   no comparison: the period has none, or the row had an earlier rank or volume but no
//            computable change (e.g. net returns left it at 0 CU) — shown as "—";
// - 'new'    truly new: no previous rank AND no previous volume;
// - 'change' a computed change, with its displayed direction (changeDirection).
// A row that shows an earlier rank is never 'new'.
export type ListChange =
  | { kind: 'none' }
  | { kind: 'new' }
  | { kind: 'change'; direction: Exclude<ChangeDirection, 'none'>; pct: number }
export function listChange({ available = true, pct, prevRank, prevVolume }: {
  available?: boolean
  pct: number | null | undefined
  prevRank: number | null | undefined
  prevVolume?: number | null
}): ListChange {
  if (!available) return { kind: 'none' }
  const direction = changeDirection(pct)
  if (direction !== 'none') return { kind: 'change', direction, pct: pct as number }
  if (prevRank == null && !(prevVolume != null && prevVolume !== 0)) return { kind: 'new' }
  return { kind: 'none' }
}

// Count nouns: "1 bundle", "2 bundles", "0 bundles". Returns the noun only, so the caller
// formats the number its own way. `pluralForm` is for irregular nouns.
export const plural = (n: number, singular: string, pluralForm = `${singular}s`): string =>
  n === 1 ? singular : pluralForm

// Dates. ISO dates (YYYY-MM-DD) are calendar days, read in UTC so a user's time zone never
// moves them a day. Day "3 Oct 2026" (export headers: "3 October 2026"), short day "3 Oct", month "October 2026", short month
// "Oct 2026", week "Week 41 · 5 Oct–11 Oct", range "19 Aug 2025 – 17 Aug 2026".
const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August',
  'September', 'October', 'November', 'December']
const MONTHS_SHORT = MONTHS_LONG.map(m => m.slice(0, 3))
const parseDay = (iso: string): Date | null => {
  const date = new Date(`${iso.slice(0, 10)}T00:00:00Z`)
  return Number.isNaN(date.getTime()) ? null : date
}

export function formatDayShort(iso: string): string {
  const d = parseDay(iso)
  return d ? `${d.getUTCDate()} ${MONTHS_SHORT[d.getUTCMonth()]}` : iso
}

export function formatDay(iso: string, { long = false } = {}): string {
  const d = parseDay(iso)
  if (!d) return iso
  return long ? `${d.getUTCDate()} ${MONTHS_LONG[d.getUTCMonth()]} ${d.getUTCFullYear()}` : `${formatDayShort(iso)} ${d.getUTCFullYear()}`
}

// Today's calendar day in the viewer's time zone, as an ISO date (export headers).
export function todayIso(now = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

// Accepts "2026-10" or any ISO date in the month.
export function formatMonth(iso: string, { short = false } = {}): string {
  const d = parseDay(iso.length === 7 ? `${iso}-01` : iso)
  if (!d) return iso
  return `${(short ? MONTHS_SHORT : MONTHS_LONG)[d.getUTCMonth()]} ${d.getUTCFullYear()}`
}

// ISO 8601 week number of a calendar day.
export function isoWeekNumber(iso: string): number {
  const d = parseDay(iso)
  if (!d) return NaN
  const thursday = new Date(d)
  thursday.setUTCDate(d.getUTCDate() + 3 - ((d.getUTCDay() + 6) % 7))
  const yearStart = Date.UTC(thursday.getUTCFullYear(), 0, 1)
  return Math.floor((thursday.getTime() - yearStart) / 86_400_000 / 7) + 1
}

// The week starting on `monday` (a Monday ISO date): "Week 41 · 5 Oct–11 Oct". Both days
// carry their month so a week across a month boundary reads "Week 40 · 29 Sep–5 Oct".
export function formatWeek(monday: string): string {
  const d = parseDay(monday)
  if (!d) return monday
  const sunday = new Date(d.getTime() + 6 * 86_400_000).toISOString().slice(0, 10)
  return `Week ${isoWeekNumber(monday)} · ${formatDayShort(monday)}–${formatDayShort(sunday)}`
}

// A range always carries its years (C17): once at the end within one year, on both sides
// across a year boundary.
export function formatRange(from: string, to: string): string {
  if (!parseDay(from) || !parseDay(to)) return `${from} – ${to}`
  return from.slice(0, 4) === to.slice(0, 4)
    ? `${formatDayShort(from)} – ${formatDay(to)}`
    : `${formatDay(from)} – ${formatDay(to)}`
}

// A timestamp (data freshness, "pinned at"): "30 July 2026, 10:46" in the viewer's time zone.
export function formatDateTime(iso: string): string | null {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getDate()} ${MONTHS_LONG[d.getMonth()]} ${d.getFullYear()}, ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

// Category names. The UI is English; Heart and older backend payloads use Swedish category
// and segment names. They are translated by the one shared taxonomy map (displayLabels.ts,
// MP-OS-191 #13); anything it does not know is shown as the source wrote it, never guessed.
// On the Overview segment cards, under the Snus and Vape headings, a few English labels read
// shorter or clearer (Olof: "all white snus, tobacco snus … och snus är rubriken").
const CARD_WORDING: Record<string, string> = {
  'All White': 'All White snus',
  'Disposable vape': 'Disposable',
  'Vape pod': 'Pod',
}
export const categoryLabel = (name: string): string => {
  const english = displayLabel(name)
  return CARD_WORDING[english] ?? english
}
